import React, { useState, useEffect } from "react";
import Calendar from "./Calendar";
import AddIcon from "@mui/icons-material/Add";
import AddEventModal from "./AddEventModal";
import AddTagModal from "./AddTagModal";
import DailySchedule from "./DailySchedule";
import { useAuth } from "../../AuthContext";
import "../../style/CalendarPage.css";

const CalendarPage = () => {
  const { userId, isAuthenticated } = useAuth();
  const [events, setEvents] = useState([]);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null); // Track selected date for Daily schedule panel
  const [tags, setTags] = useState([]);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_SOURCE;

  // Fetch events from backend (ONLY if user is logged in)
  const fetchEvents = () => {
    if (!userId) return; // Prevent fetching if userId is not available

    fetch(`${API_BASE_URL}/backend/api/Calendar/fetch_events.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "success") {
          console.log(data.events);
          setEvents(data.events);
        }
      })
      .catch((error) => console.error("Error fetching events:", error));
  };

  const fetchTags = () => {
    if (!userId) return;
  
    fetch(`${API_BASE_URL}/backend/api/Calendar/EventTags/get_tags.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setTags(data.tags);
        }
      })
      .catch((err) => console.error("Error fetching tags:", err));
  };

  // Load events when page loads
  useEffect(() => {
    if (isAuthenticated) {
      fetchEvents();
      fetchTags();
    }
  }, [isAuthenticated, userId]);

  // Handle adding event
  const handleAddEvent = (newEvent) => {
    const user_id = userId; // Replace with logged-in user ID

    const eventData = {
      user_id,
      title: newEvent.title,
      event_date: newEvent.event_date,
      event_time: newEvent.event_time,
      location: newEvent.location,
      description: newEvent.description,
      tag_id: newEvent.tag_id || null,
    };
    

    fetch(`${API_BASE_URL}/backend/api/Calendar/add_event.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(eventData),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "success") {
          fetchEvents(); // Refresh events after adding
          setIsEventModalOpen(false); // Close modal
        } else {
          console.error("Failed to add event:", data.message);
        }
      })
      .catch((error) => console.error("Error adding event:", error));
  };

  const handleDeleteEvent = (eventId) => {
    fetch(`${API_BASE_URL}/backend/api/Calendar/delete_event.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event_id: eventId, user_id: userId }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "success") {
          fetchEvents(); // Refresh calendar after deletion
        } else {
          console.error("Failed to delete event:", data.message);
        }
      })
      .catch((error) => console.error("Error deleting event:", error));
  };

  // Handle date click from Calendar
  const handleDateClick = (date) => {
    setSelectedDate(date);
  };

  // gets passed the entire updated event object
  const handleUpdateEvent = async (updatedEvent) => {
    try {
      // rebidning "id" to "event_id", and adding user_id to match backend expectations
      const eventData = {
        event_id: updatedEvent.id,
        user_id: userId,
        title: updatedEvent.title,
        event_date: updatedEvent.event_date,
        event_time: updatedEvent.event_time,
        location: updatedEvent.location,
        description: updatedEvent.description,
        tag_id: updatedEvent.tag_id || null,
      };

      const response = await fetch(`${API_BASE_URL}/backend/api/Calendar/update_event.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(eventData),
      });

      const data = await response.json();
      if (data.status === "success") {
        setEvents(events.map(event => event.id === updatedEvent.id ? updatedEvent : event));
      } else {
        console.error("Failed to update event:", data.message);
      }
    } catch (error) {
      console.error("Error updating event:", error);
    }
  };


  return (
    <div className="calendar-page-container">
      <div className="calendar-wrapper">
        <Calendar 
          events={events} 
          tags={tags}
          onDeleteEvent={handleDeleteEvent} 
          onDateClick={handleDateClick} 
          onUpdateEvent={handleUpdateEvent}
        />
      </div>

      {selectedDate && (
        <DailySchedule
          date={selectedDate}
          tags={tags}
          events={events.filter((event) => event.event_date === selectedDate)}
          onClose={() => setSelectedDate(null)}
        />
      )}

      <button className="add-event-btn" onClick={() => setIsAddMenuOpen(!isAddMenuOpen)}>
        <AddIcon />
      </button>

      {isAddMenuOpen && (
        <div className="add-menu-dropdown">
          <button onClick={() => {
            setIsEventModalOpen(true);
            setIsAddMenuOpen(false);
          }}>Add Event</button>

          <button onClick={() => {
            setIsTagModalOpen(true);
            setIsAddMenuOpen(false);
          }}>Add Category</button>
        </div>
      )}

      {isEventModalOpen && (
        <AddEventModal
          isOpen={isEventModalOpen}
          onClose={() => setIsEventModalOpen(false)}
          onAddEvent={handleAddEvent}
          tags={tags}
        />
      )}

      {isTagModalOpen && (
        <AddTagModal
          isOpen={isTagModalOpen}
          onClose={() => setIsTagModalOpen(false)}
          onTagCreated={fetchTags}
          userId={userId}
        />
      )}

    </div>
  );
};

export default CalendarPage;